import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState, useRef } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { AlertTriangle, Zap, X, ExternalLink, WifiOff, Moon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { hesapPrefix } from '../../lib/subdomain';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;
const prefix = hesapPrefix();

// Durum → görsel eşleşme
const STATE_STYLES = {
  producing: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800', text: 'text-emerald-800 dark:text-emerald-300', ping: 'bg-emerald-400', icon: Zap, label: 'Solar Aktif' },
  stopped:   { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-b-2 border-red-400 dark:border-red-700', text: 'text-red-800 dark:text-red-200', icon: AlertTriangle, label: 'Solar Uyarısı' },
  offline:   { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-b-2 border-red-500 dark:border-red-700', text: 'text-red-800 dark:text-red-200', icon: WifiOff, label: 'Cihaz Bağlantısı Kesik' },
  quiet:     { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-300 dark:border-amber-700', text: 'text-amber-800 dark:text-amber-200', icon: Moon, label: 'Sessiz Saatler' },
};

/**
 * Solar canlı durum banner'ı — sadece "solar" permission'ı olan kullanıcılara gösterilir.
 */
export default function SolarLiveBanner() {
  const { token, user } = useHesapAuth();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const prevStateRef = useRef(null);

  // Yetki kontrolü — admin/tenant sahibine her zaman, team member'a solar permission'ı varsa
  const hasSolarAccess = () => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'owner' || user.is_owner) return true;
    const perms = user.permissions || [];
    return perms.includes('solar');
  };

  useEffect(() => {
    if (!token || !hasSolarAccess()) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const r = await fetch(`${API}/solar/live-status`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setState(data);
        // Durum değiştiğinde dismiss'i sıfırla
        if (prevStateRef.current && prevStateRef.current !== data.state) {
          setDismissed(false);
        }
        prevStateRef.current = data.state;
      } catch { /* offline */ }
    };
    poll();
    const iv = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line
  }, [token, user]);

  if (!hasSolarAccess()) return null;
  if (!state || !state.configured || dismissed) return null;
  // Producing durumunda hafif banner göster; quiet'te sarı; alarm'da uyarı
  const st = STATE_STYLES[state.state];
  if (!st) return null;

  const Icon = st.icon;

  // Producing — ince yeşil bar
  if (state.state === 'producing') {
    return (
      <div className={`${st.bg} border-b ${st.border} px-3 py-1.5 text-xs flex items-center justify-between gap-2`} data-testid="solar-live-banner-active">
        <div className={`flex items-center gap-2 ${st.text}`}>
          <span className="relative flex h-2 w-2">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${st.ping} opacity-75`}></span>
            <span className={`relative inline-flex rounded-full h-2 w-2 ${st.ping.replace('400', '500')}`}></span>
          </span>
          <Icon className="h-3.5 w-3.5" />
          <span className="font-medium">{st.label}</span>
          {state.last_watts > 0 && <span>· {state.last_watts.toLocaleString('tr-TR')} W</span>}
          <span className="opacity-70 hidden sm:inline">· son sinyal {state.seconds_ago ?? 0} sn önce</span>
        </div>
        <button onClick={() => setDismissed(true)} className={`${st.text} opacity-70 hover:opacity-100`} aria-label="Kapat">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  // Quiet hours — sarı bilgi banner'ı
  if (state.state === 'quiet') {
    return (
      <div className={`${st.bg} border-b ${st.border} px-3 py-1.5 text-xs flex items-center justify-between gap-2`} data-testid="solar-live-banner-quiet">
        <div className={`flex items-center gap-2 ${st.text}`}>
          <Icon className="h-3.5 w-3.5" />
          <span className="font-medium">Solar:</span>
          <span>{state.quiet_hours_start} - {state.quiet_hours_end} arası üretim beklenmiyor</span>
        </div>
        <button onClick={() => setDismissed(true)} className={`${st.text} opacity-70 hover:opacity-100`} aria-label="Kapat">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  // Offline veya Stopped — kırmızı alarm
  return (
    <div className={`${st.bg} ${st.border} px-3 py-2 flex items-center justify-between gap-2`} data-testid={`solar-live-banner-${state.state}`}>
      <div className={`flex items-center gap-2 ${st.text} text-sm`}>
        <Icon className="h-4 w-4 flex-shrink-0" />
        <div>
          <b>{st.label}:</b> {state.message}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Link to={`${prefix}/solar`} className="inline-flex items-center gap-1 text-xs bg-red-600 hover:bg-red-700 text-white px-2.5 py-1 rounded" data-testid="solar-banner-goto">
          Detay <ExternalLink className="h-3 w-3" />
        </Link>
        <button onClick={() => setDismissed(true)} className={`${st.text} opacity-70 hover:opacity-100 p-1`} aria-label="Kapat">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
